import type { Homeless } from 'types';

export type BonusFundAvailability = {
    assignableBase: number;
    assignableExtra: number;
    blockedByYearlyFunds: boolean;
    isExtraBonusParticipant: boolean;
    mcfYearlyDepleted: boolean;
    samaritanYearlyDepleted: boolean;
    shouldUseExtraBonusPool: boolean;
    totalAssignable: number;
};

const isDepleted = (amount: number | undefined) => Number(amount) <= 0;

export const getBonusFundAvailability = (
    holder: Homeless
): BonusFundAvailability => {
    const funds = holder.funds_remaining_this_year;
    const samaritanYearlyDepleted =
        !!funds && isDepleted(funds.samaritan_left);
    const mcfYearlyDepleted = !!funds && isDepleted(funds.mcf_left);
    const monthlyBaseRemaining =
        holder.action_step_bonus.base_limit -
        holder.action_step_bonus.base_used;
    const monthlyExtraRemaining =
        (holder.action_step_bonus.scf_limit ?? 0) -
        (holder.action_step_bonus.scf_used ?? 0);
    const isExtraBonusParticipant =
        (holder.action_step_bonus.scf_limit ?? 0) > 0;
    const assignableBase = funds
        ? Math.min(monthlyBaseRemaining, Math.max(0, Number(funds.samaritan_left)))
        : monthlyBaseRemaining;
    const assignableExtra = !isExtraBonusParticipant
        ? 0
        : funds
          ? Math.min(monthlyExtraRemaining, Math.max(0, Number(funds.mcf_left)))
          : monthlyExtraRemaining;
    const shouldUseExtraBonusPool =
        assignableBase === 0 && assignableExtra > 0;
    const totalAssignable = assignableBase + assignableExtra;
    const blockedByYearlyFunds =
        totalAssignable === 0 &&
        samaritanYearlyDepleted &&
        (mcfYearlyDepleted || !isExtraBonusParticipant);

    return {
        assignableBase,
        assignableExtra,
        blockedByYearlyFunds,
        isExtraBonusParticipant,
        mcfYearlyDepleted,
        samaritanYearlyDepleted,
        shouldUseExtraBonusPool,
        totalAssignable
    };
};
